/**
 * Token Timer Service
 *
 * Simple periodic check to refresh JWT tokens before they expire.
 */

import nsApi from '@netsapiens/netsapiens-js/dist/api';
import nsToken from '@netsapiens/netsapiens-js/dist/token';
import logger from 'loglevel';

const MODULE_NAME = 'webphone/services/tokenTimer';
const log = logger.getLogger(MODULE_NAME);

// Check token every 60 seconds
const CHECK_INTERVAL_MS = 60 * 1000;

// Refresh if token expires in less than 5 minutes
const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

let checkInterval = null;

/**
 * Check if token needs refresh and refresh it if needed
 */
const checkAndRefreshToken = async () => {
  try {
    const accessToken = nsToken.get();
    const refreshToken = localStorage.getItem('ns_t_refresh');

    if (!accessToken || !refreshToken) {
      log.warn(`${MODULE_NAME} - no tokens found`);
      return;
    }

    // Decode token to get expiration
    const decoded = nsToken.decode(accessToken);
    if (!decoded || !decoded.exp) {
      log.warn(`${MODULE_NAME} - could not decode token`);
      return;
    }

    // Check if token expires soon
    const expiresAt = decoded.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeUntilExpiration = expiresAt - now;

    // If token expires in less than threshold, refresh it
    if (timeUntilExpiration < REFRESH_THRESHOLD_MS) {
      log.info(`${MODULE_NAME} - token expires soon, refreshing...`);

      const apiHash = localStorage.getItem('ns_t_hash');
      await nsApi.refreshToken(refreshToken, apiHash);

      log.info(`${MODULE_NAME} - token refreshed successfully`);
    }
  } catch (error) {
    log.error(`${MODULE_NAME} - error checking/refreshing token:`, error);

    // Clear tokens before redirect
    nsToken.remove();

    // Redirect to auth on error with expired flag
    const currentOrigin = global.location.origin;
    global.location.href = `${currentOrigin}/auth/?r=webphone&expired=true`;
  }
};

/**
 * Start the token check timer
 */
export const startTimer = () => {
  log.info(`${MODULE_NAME} - starting token check timer`);

  if (checkInterval) {
    clearInterval(checkInterval);
  }

  // Check immediately
  checkAndRefreshToken();

  // Then check every minute
  checkInterval = setInterval(checkAndRefreshToken, CHECK_INTERVAL_MS);
};

/**
 * Stop the token check timer
 */
export const stopTimer = () => {
  log.info(`${MODULE_NAME} - stopping token check timer`);

  if (checkInterval) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
};

/**
 * Initialize the token timer service
 */
export const initTokenTimer = () => {
  log.info(`${MODULE_NAME} - initializing token timer`);
  startTimer();
};
